import React, { useState, useEffect, ComponentType } from 'react';

/**
 * Creates a higher-order component that debounces rendering of the wrapped component
 * @param {ComponentType<<P>>} WrappedComponent - The component to wrap with debounced rendering
 * @param {number} [debounceTimeMs] - The debounce time in milliseconds
 * @returns A new component that debounces renders of the wrapped component
 */
function debounceRender<P extends object>(
    WrappedComponent: ComponentType<P>,
    debounceTimeMs: number = 300
) {
    return function DebouncedComponent(props: P) {
        // Store the latest props
        const [debouncedProps, setDebouncedProps] = useState<P>(props);

        // Set up the debounce effect
        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedProps(props);
            }, debounceTimeMs);

            // Clean up the timeout when props change or component unmounts
            return () => {
                clearTimeout(handler);
            };
        }, [props]);

        // Render the wrapped component with the debounced props
        return <WrappedComponent {...debouncedProps as any} />;
    };
}

export default debounceRender;